// Small Redux slice that caches profile-card payloads so that multiple
// messages referencing the same player / coach don't hammer the backend.
//
// Cache shape:
//   state.profileCards.byKey[`${type}:${id}`] = {
//     data:        normalised card payload (or null while loading)
//     status:      "loading" | "ready" | "error" | "notfound"
//     fetchedAt:   epoch ms, used for TTL invalidation
//     error:       human string, only set when status === "error"
//   }
//
// TTL is enforced by the consumer (`useProfileCard`), not the reducer —
// we keep the slice dumb so cleared/refreshed entries don't fight with
// background fetches in flight.

import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  byKey: {},
};

const profileCardsSlice = createSlice({
  name: "profileCards",
  initialState,
  reducers: {
    profileCardLoading(state, action) {
      const key = action.payload; // "player:123" or "coach:abc"
      state.byKey[key] = {
        data: null,
        status: "loading",
        fetchedAt: Date.now(),
        error: null,
      };
    },
    profileCardReady(state, action) {
      const { key, data } = action.payload;
      state.byKey[key] = {
        data,
        status: "ready",
        fetchedAt: Date.now(),
        error: null,
      };
    },
    profileCardNotFound(state, action) {
      const key = action.payload;
      state.byKey[key] = {
        data: null,
        status: "notfound",
        fetchedAt: Date.now(),
        error: null,
      };
    },
    profileCardError(state, action) {
      const { key, error } = action.payload;
      state.byKey[key] = {
        data: null,
        status: "error",
        fetchedAt: Date.now(),
        error: error || "Failed to load profile.",
      };
    },
    profileCardClear(state, action) {
      delete state.byKey[action.payload]; // forces refetch on next render
    },
  },
});

export const {
  profileCardLoading,
  profileCardReady,
  profileCardNotFound,
  profileCardError,
  profileCardClear,
} = profileCardsSlice.actions;

export default profileCardsSlice.reducer;
